import React, { useState, useEffect, useRef } from 'react';
import styles from '../../styles/scss/components/tips/influencersSection.module.scss';
import classNames from 'classnames';
import InfluencerModal from './InfluencerModal';
import { datoImage } from '../../services/datoImage';

const InfluencersSection = ({ data }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 992);
    };
    
    checkScreenSize();
    
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  
  const filteredImages = activeCategory 
    ? data.gallery.filter(img => img.category === activeCategory)
    : data.gallery;
    
  const handleCategoryClick = (category) => {
    setActiveCategory(activeCategory === category ? null : category);
    setCurrentIndex(0); 
  };
  
  const handlePrevClick = () => {
    if (isMobile) {
      setCurrentIndex((prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1));
    } else if (isTablet) {
      // For tablet: 2 images at a time
      setCurrentIndex((prev) => (prev === 0 ? filteredImages.length - 2 : prev - 1));
    } else {
      // For desktop: 3 images at a time
      setCurrentIndex((prev) => (prev === 0 ? filteredImages.length - 3 : prev - 1));
    }
  };
  
  const handleNextClick = () => {
    if (isMobile) {
      setCurrentIndex((prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1));
    } else if (isTablet) {
      // For tablet: 2 images at a time
      setCurrentIndex((prev) => (prev === filteredImages.length - 2 ? 0 : prev + 1));
    } else {
      // For desktop: 3 images at a time
      setCurrentIndex((prev) => (prev === filteredImages.length - 3 ? 0 : prev + 1));
    }
  };
  
  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };
  
  const handleImageClick = (image) => {
    setSelectedInfluencer(image);
    setModalOpen(true);
  };
  
  const getVisibleImages = () => {
    if (isMobile) {
      return [filteredImages[currentIndex]].filter(Boolean);
    } else if (isTablet) {
      return filteredImages.slice(currentIndex, currentIndex + 2);
    } else {
      return filteredImages.slice(currentIndex, currentIndex + 3);
    }
  };
  
  const visibleImages = getVisibleImages();
  
  return (
    <div className={styles.influencersSection}>
      
      <div className={styles.sectionContent}>
        <div className={styles.contentContainer}>
          <h1 className={styles.sectionTitle}>
            {data.title.includes('®') ? (
              <>
                {data.title.split('®')[0]}
                <span className={styles.trademark}>®</span>
                {data.title.split('®')[1]}
              </>
            ) : (
              data.title
            )}
          </h1>
          
          {/* Pills/Categories */}
          <div className={styles.pillsContainer}>
            {data.pills.map((category) => (
              <button 
                key={category} 
                className={classNames(styles.pill, { [styles.activePill]: activeCategory === category })}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        
        {/* Gallery */}
        <div className={styles.galleryContainer}>
          {/* Navigation Arrows - Hidden on Mobile or when 3 or fewer influencers */}
          {!isMobile && filteredImages.length > 3 && (
            <button className={styles.navArrow} onClick={handlePrevClick} aria-label="Previous influencers">
              <svg width="16" height="30" viewBox="0 0 16 30" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.arrowLeft} aria-hidden="true">
                <path d="M15 1L2 15L15 29" stroke="var(--redButton)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          
          {/* Images */}
          <div 
            className={classNames(styles.imagesContainer, {
              [styles.fewImagesContainer]: visibleImages.length < 3
            })}
            onTouchStart={(e) => {
              touchStartX.current = e.touches[0].clientX;
            }}
            onTouchMove={(e) => {
              touchEndX.current = e.touches[0].clientX;
            }}
            onTouchEnd={() => {
              const swipeDistance = touchStartX.current - touchEndX.current;
              if (Math.abs(swipeDistance) > 50) {
                if (swipeDistance > 0) {
                  handleNextClick();
                } else {
                  handlePrevClick();
                }
              }
            }}
          >
            {visibleImages.map((image) => (
              <div 
                key={image.id} 
                className={styles.imageWrapper}
                onClick={() => handleImageClick(image)}
              >
                <img 
                  src={datoImage(image.url, { w: 660 })} 
                  alt={image.alt || image.title} 
                  className={styles.image} 
                />
              </div>
            ))}
          </div>
          
          {/* Desktop Navigation Arrows - Hidden on Mobile or when 3 or fewer influencers */}
          {!isMobile && filteredImages.length > 3 && (
            <button className={styles.navArrow} onClick={handleNextClick} aria-label="Next influencers">
              <svg width="16" height="30" viewBox="0 0 16 30" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.arrowRight} aria-hidden="true">
                <path d="M1 1L14 15L1 29" stroke="var(--redButton)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
        
        {/* Mobile Dots Navigation - Only visible on Mobile */}
        {isMobile && (
          <div className={styles.dotsContainer}>
            {filteredImages.map((_, index) => (
              <button 
                key={index} 
                className={classNames(styles.dot, { [styles.activeDot]: currentIndex === index })}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Influencer Modal */}
      <InfluencerModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        influencer={selectedInfluencer} 
      />
    </div>
  );
};

export default InfluencersSection;
